import { skipToken } from "@reduxjs/toolkit/query";
import { CONTACTS_IMPORT_LIMIT } from "@repo/app-types/constants/api";
import type { CustomFieldDefinition } from "@repo/app-types/entities/customField";
import {
  CustomFieldResourceType,
  customFieldResourceTypeLabels,
  CustomFieldType,
} from "@repo/app-types/entities/customField";
import type { ProjectWithInterviewCount } from "@repo/app-types/entities/project";
import { isNotUndefined } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { FileCard } from "@repo/ui/Cards/fileCard";
import { checkIsAbortError } from "@repo/ui/helpers/errors";
import { renderPlural, Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { Link } from "@repo/ui/Link/routerLink";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { Slideover } from "@repo/ui/Slideover/slideover";
import {
  IconArrowRight,
  IconCircleCheck,
  IconCircleX,
  IconFileUpload,
  IconMail,
  IconPhone,
} from "@tabler/icons-react";
import { useVirtualizer } from "@tanstack/react-virtual";
import chunk from "lodash-es/chunk.js";
import times from "lodash-es/times.js";
import Papa from "papaparse";
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import {
  Button as AriaButton,
  Dialog,
  DialogTrigger,
  OverlayArrow,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
} from "react-aria-components";
import { useDropzone } from "react-dropzone";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";
import { z } from "zod";
import { getAxiosInstanceWithAuth } from "../../api/axiosConfig.js";
import {
  allProjectsForCompanyEndpoint,
  useProjectByIdQuery,
} from "../../api/entities/project.js";
import { useAllUserCustomFieldDefinitionsQuery } from "../../api/entities/userCustomField.js";
import { useAppDispatch } from "../../hooks/hook.js";
import {
  createProjectSearcher,
  extractCustomField,
  extractEmailAddress,
  extractNameParts,
  extractPhoneNumber,
  extractProject,
} from "./import/extraction.js";
import {
  collectEmptyColumnIndexes,
  peekColumnValues as collectExampleValues,
  extractHeaderRow,
} from "./import/helpers.js";
import {
  checkIsDynamicSourceField,
  checkIsStaticSourceField,
  collectAllSourceFields,
  generateSourceMapping,
  getMappedSourceFields,
  getSourceFieldForColumnIndex,
  parseDynamicSourceField,
  projectSourceFields,
  setSourceFieldForColumnIndex,
  SourceField,
  type DynamicSourceField,
  type SourceFieldPrefix,
  type SourceMapping,
  type StaticSourceField,
} from "./import/sourceMapping.js";

export interface ImportContactsSlideoverProp {
  isOpen: boolean;
  defaultProjectId: string;
  projectTypeFilter: "recruiting" | "training";
  onClose: () => void;
}

const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const PREVIEW_ROW_HEIGHT = 60;

// prettier-ignore
const sampleCsv = Papa.unparse([
  ["First name", "Last name", "Email address", "Phone number", "Job opening name"],
  ["Aisha", "Khan", "aisha.khan@example.org", "+1 (123) 456-7890", "Customer Support Specialist - Nashville"],
  ["Carlos", "Rodriguez", "carlos.rodriguez@example.org", "+34 123 456 789", "Customer Support Specialist - Madrid"],
]);
const sampleCsvUri = `data:text/csv;charset=utf-8,${encodeURIComponent(sampleCsv)}`;

const staticSourceFieldLabels: Record<StaticSourceField, string> = {
  [SourceField.FirstName]: "First name",
  [SourceField.LastName]: "Last name",
  [SourceField.FullName]: "Full name",
  [SourceField.Email]: "Email address",
  [SourceField.Phone]: "Phone number",
  [SourceField.ProjectId]: "Project ID",
  [SourceField.ProjectName]: "Project name",
  [SourceField.JobTitle]: "Job title",
  [SourceField.JobLocation]: "Job location",
};

function getSourceFieldLabel(
  userCustomFields: readonly CustomFieldDefinition[],
  field: SourceField,
): string {
  if (checkIsStaticSourceField(field)) {
    return staticSourceFieldLabels[field];
  }

  const { id } = parseDynamicSourceField(field) satisfies {
    prefix: typeof SourceFieldPrefix.CustomField;
  };
  const customField = userCustomFields.find((field) => field.id === id);
  return customField
    ? `[${customFieldResourceTypeLabels[customField.resource_type]}] ${customField.display_name}`
    : `Unknown custom field (${id})`;
}

// TODO: Enrich this object with the source and parsing errors so we can surface these to the user.
interface TransformedRow {
  originalIndex: number;
  firstName: string | undefined;
  lastName: string | undefined;
  email: string | undefined;
  phone: string | undefined;
  project:
    | {
        id: string;
        name: string;
      }
    | undefined;
  customFields: readonly TransformedCustomField[];
}

interface TransformedCustomField {
  id: string;
  value: string | number | boolean | null;
}

function checkIsRowValid(row: TransformedRow): boolean {
  return !!(row.email || row.phone) && !!row.project;
}

const PreviewFilterKey = {
  All: "all",
  Invalid: "invalid",
  Pending: "pending",
  Failed: "failed",
  Imported: "imported",
} as const;

type PreviewFilterKey =
  (typeof PreviewFilterKey)[keyof typeof PreviewFilterKey];

const PreviewFilterColor = {
  Gray: "gray",
  Red: "red",
  Blue: "blue",
  Green: "green",
} as const;

type PreviewFilterColor =
  (typeof PreviewFilterColor)[keyof typeof PreviewFilterColor];

interface PreviewFilterOption {
  key: PreviewFilterKey;
  label: string;
  color: PreviewFilterColor;
  count: number;
  isVisible: boolean;
  rowPredicate: (rowIndex: number) => boolean;
}

const previewFilterButtonGroup = tv({
  base: "inline-flex -space-x-px rounded-md shadow-xs",
});

const previewFilterButton = tv({
  slots: {
    base: "group flex items-center space-x-2 bg-white px-3 py-1.5 text-sm font-semibold text-foreground ring-1 ring-border-strong ring-inset",
    label: "truncate",
    pill: "flex items-center rounded-full px-2 py-1 text-xs font-medium",
  },
  variants: {
    placement: {
      start: "rounded-l-md",
      center: "",
      end: "rounded-r-md",
    },
    color: {
      [PreviewFilterColor.Gray]: {
        base: "hover:bg-gray-50 selected:bg-gray-600 selected:text-white selected:ring-gray-600",
        pill: "bg-gray-100 text-foreground group-selected:bg-gray-500 group-selected:text-white",
      },
      [PreviewFilterColor.Red]: {
        base: "hover:bg-red-50 selected:bg-destructive selected:text-white selected:ring-red-600",
        pill: "bg-red-100 text-red-800 group-selected:bg-red-500 group-selected:text-white",
      },
      [PreviewFilterColor.Blue]: {
        base: "hover:bg-blue-50 selected:bg-blue-600 selected:text-white selected:ring-blue-600",
        pill: "bg-blue-100 text-blue-800 group-selected:bg-blue-500 group-selected:text-white",
      },
      [PreviewFilterColor.Green]: {
        base: "hover:bg-green-50 selected:bg-success selected:text-white selected:ring-green-600",
        pill: "bg-green-100 text-green-800 group-selected:bg-green-500 group-selected:text-white",
      },
    },
  },
});

function computePreviewFilterButtonPlacement(index: number, length: number) {
  if (index === 0) {
    return "start";
  } else if (index === length - 1) {
    return "end";
  } else {
    return "center";
  }
}

export const ImportContactsSlideover: FC<ImportContactsSlideoverProp> = (
  props,
) => {
  const { data: userCustomFields = [] } =
    useAllUserCustomFieldDefinitionsQuery();

  const [file, setFile] = useState<File | undefined>(undefined);
  const [isParsing, setIsParsing] = useState<boolean>(false);
  const [header, setHeader] = useState<string[]>([]);
  const [data, setData] = useState<string[][]>([]);
  const [emptyColumnIndexes, setEmptyColumnIndexes] = useState<Set<number>>(
    new Set(),
  );
  const [mapping, setMapping] = useState<SourceMapping>({});
  const mappedFields = getMappedSourceFields(mapping);
  const isMappingValid = [SourceField.Email, SourceField.Phone].some((field) =>
    mappedFields.includes(field),
  );
  const isMappingProjects = projectSourceFields.some((field) =>
    mappedFields.includes(field),
  );

  const sourceFieldOptions = useMemo<
    readonly SimpleSelectOption<SourceField | null>[]
  >(
    () => [
      {
        label: "Don't import",
        value: null,
      },
      ...collectAllSourceFields(userCustomFields).map((value) => ({
        label: getSourceFieldLabel(userCustomFields, value),
        value,
      })),
    ],
    [userCustomFields],
  );

  const dispatch = useAppDispatch();

  // Fetch all projects to support mapping a CSV column to a project.
  // Don't mess the legacy Redux state because it triggers side-effects in parent components.
  const [allProjects, setAllProjects] = useState<
    readonly ProjectWithInterviewCount[] | undefined
  >(undefined);

  useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    const fetchAllProjects = async (
      typeFilter: "recruiting" | "training",
      signal: AbortSignal,
    ) => {
      // Fetch the first page (or whatever is in cache)
      let action = dispatch(
        allProjectsForCompanyEndpoint.initiate({
          typeFilter: typeFilter === "training" ? "training" : undefined,
          limit: 100,
        }),
      );
      let result = await action;
      signal.throwIfAborted();

      // Fetch all remaining pages
      while (result.hasNextPage) {
        action = dispatch(
          allProjectsForCompanyEndpoint.initiate(action.arg, {
            direction: "forward",
          }),
        );
        result = await action;
        signal.throwIfAborted();
      }

      // Get all the data, or throw an error
      const data = await action.unwrap();
      signal.throwIfAborted();

      return data.pages.flatMap((page) => page.projects);
    };

    const abortController = new AbortController();

    fetchAllProjects(props.projectTypeFilter, abortController.signal)
      .then((allProjects) => {
        setAllProjects(allProjects);
      })
      .catch((error) => {
        if (checkIsAbortError(error)) {
          return;
        }

        console.error(
          "Failed to fetch projects for contact import slideover",
          error,
        );
      });

    return () => {
      abortController.abort();
    };
  }, [dispatch, props.isOpen, props.projectTypeFilter]);

  // Separately, fetch the project the import slideover was opened from.
  const { data: defaultProject } = useProjectByIdQuery(
    props.isOpen ? props.defaultProjectId : skipToken,
  );

  const projects = useMemo(() => {
    return [
      ...(defaultProject ? [defaultProject] : []),
      ...(allProjects?.filter((project) => project.id !== defaultProject?.id) ??
        []),
    ];
  }, [defaultProject, allProjects]);

  useEffect(() => {
    if (!props.isOpen) {
      // Reset state when the popover is closed.
      // TODO: Fix the component lifecycle of slideovers so that they're unmounted when closed.
      setFile(undefined);
      setIsParsing(false);
    }
  }, [props.isOpen]);

  useEffect(() => {
    // Reset state when the file is removed.
    // TODO: Do this better.
    if (!file) {
      setHeader([]);
      setData([]);
      setEmptyColumnIndexes(new Set());
      setMapping({});
      setImportedRowIndexes(new Set());
      setFailedRows(new Map());
    }
  }, [file]);

  const {
    getRootProps: getDropzoneRootProps,
    getInputProps: getDropzoneInputProps,
    isDragAccept: isDropzoneDragAccept,
    isDragReject: isDropzoneDragReject,
  } = useDropzone({
    onDropAccepted: async (acceptedFiles) => {
      setIsParsing(true);
      try {
        const acceptedFile = acceptedFiles[0];
        if (acceptedFile === undefined) {
          return;
        }
        const [rows] = await Promise.all([
          new Promise<string[][]>((resolve, reject) => {
            const rows: string[][] = [];

            Papa.parse<string[]>(acceptedFile, {
              skipEmptyLines: true,
              step: (results) => {
                rows.push(results.data);
              },
              complete: (results) => {
                // TODO: Handle results.errors.
                resolve(rows);
              },
              error: (error) => {
                reject(error);
              },
            });
          }),
          // Avoid flashing the loader for small imports.
          new Promise((resolve) => setTimeout(resolve, 500)),
        ]);

        setFile(acceptedFile);

        const [header, data] = extractHeaderRow(rows);
        setHeader(header);
        setData(data);

        const emptyColumnIndexes = new Set(
          collectEmptyColumnIndexes(header, data),
        );
        setEmptyColumnIndexes(emptyColumnIndexes);

        // TODO: Also look at the data to detect email and phone columns.
        setMapping(generateSourceMapping(header, emptyColumnIndexes));
      } finally {
        setIsParsing(false);
      }
    },
    maxFiles: 1,
    maxSize: MAX_SIZE_BYTES,
    accept: { "text/csv": [".csv"] },
  });

  // Used to fuzzy search job openings.
  const searchProjects = useMemo(
    () => createProjectSearcher(projects),
    [projects],
  );

  // Apply the mappings and transform the data so we can preview the import.
  const transformedData: readonly TransformedRow[] = useMemo(() => {
    const getValue = (row: string[], field: SourceField) => {
      const index = mapping[field];
      return index === undefined ? undefined : row[index];
    };

    return data.map((row, index) => {
      // Determine the first and last name from whatever combination of name fields we have.
      const { firstName, lastName } = extractNameParts({
        firstName: getValue(row, SourceField.FirstName),
        lastName: getValue(row, SourceField.LastName),
        fullName: getValue(row, SourceField.FullName),
      });

      // Find the project ID from whatever combination of job-related fields we have.
      const project = isMappingProjects
        ? extractProject(projects, searchProjects, {
            id: getValue(row, SourceField.ProjectId),
            name: getValue(row, SourceField.ProjectName),
            role: getValue(row, SourceField.JobTitle),
            location: getValue(row, SourceField.JobLocation),
          })
        : defaultProject;

      const customFields = getMappedSourceFields(mapping)
        .filter(checkIsDynamicSourceField)
        .map((dynamicSourceField) => {
          const { id } = parseDynamicSourceField(dynamicSourceField) satisfies {
            prefix: typeof SourceFieldPrefix.CustomField;
          };
          const customField = userCustomFields.find((field) => field.id === id);
          const value = getValue(row, dynamicSourceField);

          return {
            id,
            value: extractCustomField(customField?.field_type, value),
          };
        });

      return {
        originalIndex: index,
        firstName,
        lastName,
        email: extractEmailAddress(getValue(row, SourceField.Email)),
        phone: extractPhoneNumber(getValue(row, SourceField.Phone)),
        project,
        customFields,
      };
    });
  }, [
    data,
    mapping,
    defaultProject,
    projects,
    searchProjects,
    userCustomFields,
  ]);

  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [importedRowIndexes, setImportedRowIndexes] = useState<Set<number>>(
    new Set(),
  );
  const [failedRows, setFailedRows] = useState<Map<number, { reason: string }>>(
    new Map(),
  );

  const onImportClick = async () => {
    setIsImporting(true);
    try {
      const axios = await getAxiosInstanceWithAuth();

      const contacts = transformedData
        .map((row, originalIndex) => {
          if (!checkIsRowValid(row)) {
            return undefined;
          }

          return {
            _original_index: originalIndex,
            first_name: row.firstName,
            last_name: row.lastName,
            email: row.email,
            phone_number: row.phone,
            project_id: row.project?.id,
            custom_fields: row.customFields
              .map(
                makeMapTransformedCustomFieldToPublicApiPayload(
                  userCustomFields,
                  CustomFieldResourceType.CONTACT,
                ),
              )
              .filter(isNotUndefined),
            interview_custom_fields: row.customFields
              .map(
                makeMapTransformedCustomFieldToPublicApiPayload(
                  userCustomFields,
                  CustomFieldResourceType.INTERVIEW,
                ),
              )
              .filter(isNotUndefined),
          };
        })
        .filter(isNotUndefined);

      const batches = chunk(contacts, CONTACTS_IMPORT_LIMIT);

      for (const batch of batches) {
        const { data } = await axios.post("/contacts/import", {
          contacts: batch,
        });
        const {
          results: { imported_count, failed_contacts },
        } = z
          .object({
            results: z.object({
              imported_count: z.number(),
              failed_contacts: z.array(
                z.object({
                  contact: z.object({
                    _original_index: z.number(),
                  }),
                  reason: z.string(),
                }),
              ),
            }),
          })
          .parse(data);

        const failedRows = new Map(
          failed_contacts.map(
            (failedContact) =>
              [
                failedContact.contact._original_index,
                { reason: failedContact.reason },
              ] as const,
          ),
        );

        setImportedRowIndexes((prevImportedIndexes) => {
          const nextImportedIndexes = new Set(prevImportedIndexes);

          for (const { _original_index: rowIndex } of batch) {
            nextImportedIndexes.add(rowIndex);
          }

          for (const failedRow of failedRows.keys()) {
            nextImportedIndexes.delete(failedRow);
          }

          return nextImportedIndexes;
        });
        setFailedRows((prevFailedRows) => {
          const nextFailedRows = new Map(prevFailedRows);

          for (const { _original_index: rowIndex } of batch) {
            const failedRow = failedRows.get(rowIndex);

            if (failedRow) {
              nextFailedRows.set(rowIndex, failedRow);
            } else {
              nextFailedRows.delete(rowIndex);
            }
          }

          return nextFailedRows;
        });
      }
    } finally {
      setIsImporting(false);
    }
  };

  const totalRowCount = transformedData.length;
  const [invalidRowCount, failedRowCount, importedRowCount, pendingRowCount] =
    useMemo(
      () =>
        transformedData.reduce(
          ([invalid, failed, imported, pending], row, index) => {
            if (!checkIsRowValid(row)) {
              return [invalid + 1, failed, imported, pending];
            }

            if (failedRows.has(index)) {
              return [invalid, failed + 1, imported, pending];
            }

            if (importedRowIndexes.has(index)) {
              return [invalid, failed, imported + 1, pending];
            }

            return [invalid, failed, imported, pending + 1];
          },
          [0, 0, 0, 0],
        ),
      [transformedData, failedRows, importedRowIndexes],
    );

  const previewFilterOptions: readonly PreviewFilterOption[] = [
    {
      key: PreviewFilterKey.All,
      label: "All",
      color: PreviewFilterColor.Gray,
      count: totalRowCount,
      isVisible: true,
      rowPredicate: () => true,
    },
    {
      key: PreviewFilterKey.Invalid,
      label: "Invalid",
      color: PreviewFilterColor.Red,
      count: invalidRowCount,
      isVisible: invalidRowCount > 0 && invalidRowCount < totalRowCount,
      rowPredicate: (rowIndex: number) => {
        const row = transformedData[rowIndex];
        return (
          row !== undefined &&
          !checkIsRowValid(row) &&
          !failedRows.has(rowIndex) &&
          !importedRowIndexes.has(rowIndex)
        );
      },
    },
    {
      key: PreviewFilterKey.Pending,
      label: "Ready to import",
      color: PreviewFilterColor.Blue,
      count: pendingRowCount,
      isVisible: pendingRowCount > 0 && pendingRowCount < totalRowCount,
      rowPredicate: (rowIndex: number) => {
        const row = transformedData[rowIndex];
        return (
          row !== undefined &&
          checkIsRowValid(row) &&
          !failedRows.has(rowIndex) &&
          !importedRowIndexes.has(rowIndex)
        );
      },
    },
    {
      key: PreviewFilterKey.Failed,
      label: "Failed",
      color: PreviewFilterColor.Red,
      count: failedRowCount,
      isVisible: failedRowCount > 0 && failedRowCount < totalRowCount,
      rowPredicate: (rowIndex: number) => failedRows.has(rowIndex),
    },
    {
      key: PreviewFilterKey.Imported,
      label: "Imported",
      color: PreviewFilterColor.Green,
      count: importedRowCount,
      isVisible: importedRowCount > 0 && importedRowCount < totalRowCount,
      rowPredicate: (rowIndex: number) => importedRowIndexes.has(rowIndex),
    },
  ] as const;
  const [selectedPreviewFilterKey, setSelectedPreviewFilterKey] =
    useState<PreviewFilterKey>(PreviewFilterKey.All);
  const visiblePreviewFilterOptions = previewFilterOptions.filter(
    (option) => option.key === selectedPreviewFilterKey || option.isVisible,
  );

  const previewData = useMemo(() => {
    const predicate =
      previewFilterOptions.find(
        (option) => option.key === selectedPreviewFilterKey,
      )?.rowPredicate ?? (() => true);

    return transformedData.filter((_, index) => predicate(index));
  }, [transformedData, selectedPreviewFilterKey]);

  const scrollElementRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: previewData.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => PREVIEW_ROW_HEIGHT,
    overscan: 10,
  });

  return (
    <Slideover
      shouldShow={props.isOpen}
      onClose={props.onClose}
      title="Import contacts"
      buttons={
        failedRowCount + importedRowCount > 0 ? (
          <div className="flex items-center space-x-4">
            <div>
              {failedRowCount > 0 ? (
                <div className="flex items-center space-x-2">
                  <IconCircleX className="size-5 shrink-0 text-destructive" />
                  <span className="text-sm">
                    {renderPlural(failedRowCount, {
                      one: "Failed to import 1 contact",
                      other: (count) => `Failed to import ${count} contacts`,
                    })}
                  </span>
                </div>
              ) : null}
              {importedRowCount > 0 ? (
                <div className="flex items-center space-x-2">
                  <IconCircleCheck className="size-5 shrink-0 text-success" />
                  <span className="text-sm">
                    {renderPlural(importedRowCount, {
                      one: "Imported 1 contact successfully",
                      other: (count) =>
                        `Imported ${count} contacts successfully`,
                    })}
                  </span>
                </div>
              ) : null}
            </div>
            <Button variant={ButtonVariant.Primary} onClick={props.onClose}>
              Close
            </Button>
          </div>
        ) : (
          <>
            <Button variant={ButtonVariant.Secondary} onClick={props.onClose}>
              Cancel
            </Button>
            <Button
              variant={ButtonVariant.Primary}
              isDisabled={pendingRowCount === 0}
              isLoading={isImporting}
              onClick={onImportClick}
            >
              {renderPlural(pendingRowCount, {
                zero: "Import contacts",
                one: "Import 1 contact",
                other: (count) => `Import ${count} contacts`,
              })}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-auto flex-col overflow-hidden">
        {file && transformedData ? (
          <div className="flex flex-auto flex-col overflow-hidden bg-background">
            <div className="grid grid-cols-[min-content_minmax(0,1fr)] overflow-hidden">
              <div className="flex flex-col overflow-hidden">
                <div className="p-6">
                  <FileCard
                    name={file.name}
                    onDelete={() => void setFile(undefined)}
                  />
                </div>
                <div className="space-y-2 px-6 pb-4">
                  <Label size={Size.MEDIUM}>Map CSV columns</Label>
                  <p className="text-sm text-muted-foreground">
                    Specify how each column should be imported. Either email
                    address or phone number is required.
                  </p>
                </div>
                <div className="overflow-y-auto">
                  <div className="grid grid-cols-[12rem_12rem] items-start gap-2 px-6 pb-6 text-sm">
                    {header.map((headerLabel, index) => (
                      <Fragment key={index}>
                        <div className="pt-2 font-mono">
                          <div className="truncate font-semibold">
                            {headerLabel}
                          </div>
                          <div className="min-h-8">
                            {emptyColumnIndexes.has(index) ? (
                              <div className="text-xs text-muted-foreground">
                                (empty column)
                              </div>
                            ) : (
                              collectExampleValues(data, index, 2).map(
                                (value, index) => (
                                  <div
                                    key={index}
                                    className="truncate text-xs text-muted-foreground"
                                  >
                                    {value}
                                  </div>
                                ),
                              )
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 overflow-hidden">
                          <IconArrowRight className="size-4 shrink-0" />
                          <SimpleSelect
                            label={`Mapping for ${headerLabel}`}
                            isLabelHidden
                            isDisabled={emptyColumnIndexes.has(index)}
                            placeholder="Don't import"
                            options={sourceFieldOptions}
                            disabledValues={mappedFields.filter(
                              (field) =>
                                field !==
                                getSourceFieldForColumnIndex(mapping, index),
                            )}
                            value={getSourceFieldForColumnIndex(mapping, index)}
                            onChange={(newValue) => {
                              setMapping((prevMapping) =>
                                setSourceFieldForColumnIndex(
                                  prevMapping,
                                  index,
                                  newValue || null,
                                ),
                              );
                            }}
                          />
                        </div>
                      </Fragment>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col overflow-hidden">
                {!isMappingValid ? (
                  <div className="flex flex-auto items-center justify-center">
                    <div className="max-w-96 text-center text-muted-foreground">
                      Use the dropdowns on the left to specify from which
                      columns to import the contact&apos;s{" "}
                      <span className="font-semibold">email address</span> or{" "}
                      <span className="font-semibold">phone number</span>.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="p-6">
                      <ToggleButtonGroup
                        className={previewFilterButtonGroup({
                          className:
                            visiblePreviewFilterOptions.length === 1
                              ? "invisible"
                              : "",
                        })}
                        selectedKeys={[selectedPreviewFilterKey]}
                        onSelectionChange={(keys) =>
                          void setSelectedPreviewFilterKey(
                            keys.values().next().value as any,
                          )
                        }
                      >
                        {visiblePreviewFilterOptions.map(
                          (option, index, array) => {
                            const style = previewFilterButton({
                              placement: computePreviewFilterButtonPlacement(
                                index,
                                array.length,
                              ),
                              color: option.color,
                            });

                            return (
                              <ToggleButton
                                key={option.key}
                                id={option.key}
                                className={style.base()}
                              >
                                <span className={style.label()}>
                                  {option.label}
                                </span>
                                <span className={style.pill()}>
                                  {option.count}
                                </span>
                              </ToggleButton>
                            );
                          },
                        )}
                      </ToggleButtonGroup>
                    </div>
                    <div className="space-y-2 px-6 pb-4">
                      <div className="flex items-center space-x-4">
                        <Label size={Size.MEDIUM}>Preview</Label>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Review rows for accuracy to ensure contacts will be
                        imported correctly. Click on a row for details.
                      </p>
                    </div>
                    <div
                      ref={scrollElementRef}
                      className="h-full overflow-auto"
                    >
                      <div className="px-6 pb-4 text-sm">
                        <div
                          style={{
                            height: `${virtualizer.getTotalSize()}px`,
                          }}
                        >
                          {virtualizer
                            .getVirtualItems()
                            .map((virtualRow, index) => {
                              const row = previewData[virtualRow.index];
                              if (row === undefined) {
                                return null;
                              }

                              return (
                                <div
                                  key={index}
                                  className="flex overflow-hidden pb-1"
                                  style={{
                                    height: `${virtualRow.size}px`,
                                    transform: `translateY(${
                                      virtualRow.start - index * virtualRow.size
                                    }px)`,
                                  }}
                                >
                                  <DialogTrigger>
                                    <AriaButton
                                      className={twMerge(
                                        "grid flex-auto grid-cols-[minmax(0,2fr)_minmax(0,4fr)_minmax(0,3fr)] items-center gap-2 rounded-md p-2 text-left text-foreground shadow-xs",
                                        "border border-border-strong bg-white",
                                        importedRowIndexes.has(
                                          row.originalIndex,
                                        )
                                          ? "border-green-500 bg-green-50"
                                          : "",
                                        failedRows.has(row.originalIndex)
                                          ? "border-red-500 bg-red-50"
                                          : "",
                                      )}
                                    >
                                      <div>
                                        <div className="truncate">
                                          {row.firstName ? (
                                            <span className="font-semibold">
                                              {row.firstName}
                                            </span>
                                          ) : (
                                            <span className="text-gray-400 italic">
                                              no first name
                                            </span>
                                          )}
                                        </div>
                                        <div className="truncate">
                                          {row.lastName ? (
                                            <span>{row.lastName}</span>
                                          ) : (
                                            <span className="text-gray-400 italic">
                                              no last name
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div>
                                        {row.email || row.phone ? (
                                          <>
                                            <div className="flex items-center space-x-2">
                                              {row.email ? (
                                                <>
                                                  <IconMail className="size-3 shrink-0 text-muted-foreground" />
                                                  <span className="truncate">
                                                    {row.email}
                                                  </span>
                                                </>
                                              ) : (
                                                <>
                                                  <IconMail className="size-3 shrink-0 text-gray-300" />
                                                  <span className="truncate text-gray-400 italic">
                                                    no email address
                                                  </span>
                                                </>
                                              )}
                                            </div>
                                            <div className="flex items-center space-x-2">
                                              {row.phone ? (
                                                <>
                                                  <IconPhone className="size-3 shrink-0 text-muted-foreground" />
                                                  <span className="truncate">
                                                    {row.phone}
                                                  </span>
                                                </>
                                              ) : (
                                                <>
                                                  <IconPhone className="size-3 shrink-0 text-gray-300" />
                                                  <span className="truncate text-gray-400 italic">
                                                    no phone number
                                                  </span>
                                                </>
                                              )}
                                            </div>
                                          </>
                                        ) : (
                                          <span className="text-red-500 italic">
                                            email address or phone number is
                                            required
                                          </span>
                                        )}
                                      </div>
                                      <div className="line-clamp-2">
                                        {row.project ? (
                                          <span>{row.project.name}</span>
                                        ) : (
                                          <span className="text-red-500 italic">
                                            project not found
                                          </span>
                                        )}
                                      </div>
                                    </AriaButton>

                                    <Popover
                                      placement="start"
                                      offset={16}
                                      className="group rounded-md bg-white shadow-lg ring-1 ring-black/5 data-[entering]:animate-popover-enter data-[exiting]:animate-popover-exit"
                                    >
                                      <OverlayArrow>
                                        <svg
                                          viewBox="0 0 12 12"
                                          className="block size-4 group-placement-left:-rotate-90 group-placement-right:rotate-270 group-placement-bottom:rotate-180"
                                        >
                                          <path
                                            className="fill-white stroke-black/5"
                                            d="M0 0L6 6L12 0"
                                          />
                                        </svg>
                                      </OverlayArrow>
                                      <Dialog className="space-y-4 p-4 text-sm">
                                        {importedRowIndexes.has(
                                          row.originalIndex,
                                        ) ? (
                                          <div className="font-semibold text-success">
                                            Imported successfully
                                          </div>
                                        ) : failedRows.has(
                                            row.originalIndex,
                                          ) ? (
                                          <div className="space-y-2 text-destructive">
                                            <div className="font-semibold">
                                              Failed to import:{" "}
                                            </div>
                                            <div>
                                              {failedRows.get(row.originalIndex)
                                                ?.reason || "(unknown reason)"}
                                            </div>
                                          </div>
                                        ) : null}

                                        {maybeRenderCustomFieldsPreview(
                                          userCustomFields,
                                          mapping,
                                          row.customFields,
                                        )}

                                        <div className="space-y-2">
                                          <div className="font-semibold">
                                            Original CSV row values:
                                          </div>
                                          <div className="grid max-h-80 max-w-80 grid-cols-[minmax(0,min-content)_minmax(0,min-content)] gap-x-1 overflow-auto">
                                            {data[row.originalIndex]?.map(
                                              (value, index) => (
                                                <Fragment key={index}>
                                                  <div className="truncate font-mono text-xs font-semibold">
                                                    {header[index]}
                                                  </div>
                                                  <div className="truncate font-mono text-xs">
                                                    {value}
                                                  </div>
                                                </Fragment>
                                              ),
                                            )}
                                          </div>
                                        </div>
                                      </Dialog>
                                    </Popover>
                                  </DialogTrigger>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : isParsing ? (
          <div className="grid grid-cols-5 gap-4 p-6">
            {times(50).map((index) => (
              <div
                key={index}
                className="h-5 animate-pulse rounded-full bg-gray-200"
              />
            ))}
          </div>
        ) : (
          <div className="space-y-6 p-6">
            <div className="space-y-2 text-sm text-foreground">
              <p>
                Add many contacts to this and other projects by uploading a CSV
                file.
              </p>
              <p>
                The file must include an email address or phone number for each
                contact and may also include a first name, last name, and/or
                full name. To add contacts to many projects at once, the file
                must include a project ID or project name.{" "}
                <Link
                  className="font-medium text-blue-600 hover:text-blue-500"
                  href={sampleCsvUri}
                >
                  Download this sample CSV file
                </Link>{" "}
                for reference.
              </p>
              <p>
                You'll be able to customize and preview the import on the next
                step.
              </p>
            </div>
            <div
              {...getDropzoneRootProps()}
              className={twMerge(
                "group flex cursor-pointer justify-center rounded-lg border border-dashed border-border-strong px-6 py-10 hover:border-gray-400 hover:bg-gray-50",
                isDropzoneDragAccept ? "border-green-500 bg-green-100" : "",
                isDropzoneDragReject ? "border-red-500 bg-red-100" : "",
              )}
            >
              <div className="text-center">
                <IconFileUpload
                  aria-hidden="true"
                  className="mx-auto h-12 w-12 text-gray-300"
                />
                <div className="mt-4 flex text-sm leading-6 text-gray-600">
                  <label
                    className="cursor-pointer font-semibold text-indigo-600 group-hover:text-indigo-500"
                    htmlFor="file-upload"
                  >
                    <span>Upload a CSV file</span>
                    <input
                      {...getDropzoneInputProps()}
                      className="sr-only"
                      id="file-upload"
                      name="file-upload"
                      type="file"
                    />
                  </label>
                  <p className="pl-1">or drag and drop.</p>
                </div>
                <p className="text-xs leading-5 text-gray-600">
                  Up to {(MAX_SIZE_BYTES / (1024 * 1024)).toFixed()} MB
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Slideover>
  );
};

function maybeRenderCustomFieldsPreview(
  userCustomFields: readonly CustomFieldDefinition[],
  mapping: SourceMapping,
  customFields: readonly TransformedCustomField[],
): ReactNode {
  const mappedDynamicSourceFields = getMappedSourceFields(mapping).filter(
    checkIsDynamicSourceField,
  );

  if (mappedDynamicSourceFields.length === 0) {
    return null;
  }

  const renderValue = (dynamicSourceField: DynamicSourceField) => {
    const { id } = parseDynamicSourceField(dynamicSourceField) satisfies {
      prefix: typeof SourceFieldPrefix.CustomField;
    };

    const value = customFields.find((field) => field.id === id)?.value;
    if (value === undefined || value === null) {
      return "(empty)";
    }

    const customField = userCustomFields.find((field) => field.id === id);
    switch (customField?.field_type) {
      case CustomFieldType.Text:
        return value;

      case CustomFieldType.Boolean:
        return value ? "Yes" : "No";

      case CustomFieldType.Number:
        return String(value);

      case CustomFieldType.Date:
      case CustomFieldType.DateAndTime:
        return String(value);

      default:
        return <code>{JSON.stringify(value)}</code>;
    }
  };

  return (
    <div className="mt-4 space-y-2">
      <div className="font-semibold">Custom fields to import:</div>
      <div className="grid max-w-80 grid-cols-[minmax(0,min-content)_minmax(0,min-content)] gap-x-1">
        {mappedDynamicSourceFields.map((dynamicSourceField) => (
          <Fragment key={dynamicSourceField}>
            <div className="truncate text-xs font-semibold">
              {getSourceFieldLabel(userCustomFields, dynamicSourceField)}
            </div>
            <div className="truncate text-xs">
              {renderValue(dynamicSourceField)}
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function makeMapTransformedCustomFieldToPublicApiPayload(
  userCustomFields: readonly CustomFieldDefinition[],
  resourceType: CustomFieldResourceType,
) {
  return (item: TransformedCustomField) => {
    const customField = userCustomFields.find((field) => field.id === item.id);

    // Filter out custom fields that do not belong to the specified resource type.
    if (customField?.resource_type !== resourceType) {
      return undefined;
    }

    return {
      // Map the custom field definition ID to the field name, which is what the public API uses as the key/identifier.
      key: customField.field_name,
      value: item.value,
    };
  };
}
